import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Chip,
  Tooltip,
  Button,
  useTheme,
  alpha,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Fade,
  Backdrop,
  Popover,
  List,
  ListItem,
  ListItemText,
  Divider,
  FormControlLabel,
  Switch,
  Checkbox,
  Avatar
} from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Today as TodayIcon,
  Add as AddIcon,
  Group as GroupIcon,
  VideoCall as VideoCallIcon,
  LocationOn as LocationIcon,
  Person as PersonIcon,
  Delete as DeleteIcon,
  Lock as LockIcon,
  Repeat as RepeatIcon,
  Schedule as ScheduleIcon,
  Event as EventIcon,
  ContentCopy as ContentCopyIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { 
  format, 
  startOfMonth, 
  endOfMonth, 
  startOfWeek, 
  endOfWeek, 
  startOfDay,
  endOfDay,
  addDays, 
  addMonths, 
  subMonths, 
  addWeeks,
  subWeeks,
  addYears,
  subYears,
  isSameMonth, 
  isSameDay, 
  isToday,
  getHours,
  setHours,
  eachHourOfInterval
} from 'date-fns';
import { supabase } from '../../../supabaseClient';

const eventColors = {
  confirmed: '#4caf50',
  tentative: '#ff9800',
  cancelled: '#9e9e9e'
};

const dynamicColors = [
  '#1976d2', '#388e3c', '#f57c00', '#7b1fa2', '#c2185b', '#00796b',
  '#5d4037', '#616161', '#0097a7', '#fbc02d', '#e64a19', '#512da8',
  '#303f9f', '#689f38', '#f9a825', '#8e24aa', '#d32f2f', '#00695c',
  '#2e7d32', '#1565c0', '#ef6c00', '#6a1b9a', '#ad1457', '#00838f',
  '#4e342e', '#424242', '#006064', '#f57f17', '#bf360c', '#4527a0',
  '#283593', '#558b2f', '#ff8f00', '#7b1fa2', '#c62828', '#004d40',
  '#3e2723', '#212121', '#00acc1', '#fdd835', '#d84315', '#673ab7',
  '#1a237e', '#33691e', '#ff6f00', '#4a148c', '#b71c1c', '#00251a'
];

const CalendarContainer = styled(Paper)(({ theme }) => ({
  borderRadius: 12,
  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
  background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 1)}, ${alpha(theme.palette.grey[50], 0.5)})`,
  boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.12)}`,
  overflow: 'hidden',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.18)}`,
  }
}));

// Profile Avatar Component
const ProfileAvatar = ({ userId, user, size = 28, sx = {} }) => {
  const [avatarUrl, setAvatarUrl] = useState('');
  
  useEffect(() => {
    if (!userId) return;
    
    const loadAvatar = async () => {
      try {
        const { data: files, error } = await supabase.storage
          .from('profile-avatars')
          .list(`${userId}/`, {
            limit: 1,
            sortBy: { column: 'created_at', order: 'desc' }
          });

        if (error || !files || files.length === 0) return;

        const fileName = `${userId}/${files[0].name}`;
        const { data, error: signedUrlError } = await supabase.storage
          .from('profile-avatars')
          .createSignedUrl(fileName, 3600);
        
        if (!signedUrlError && data?.signedUrl) {
          setAvatarUrl(data.signedUrl);
        }
      } catch (err) {
        // Silent fail
      }
    };

    loadAvatar();
  }, [userId]);

  const getInitials = () => {
    if (user?.full_name) {
      return user.full_name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    }
    if (user?.email) {
      return user.email[0].toUpperCase();
    }
    return '?';
  };

  return (
    <Avatar
      src={avatarUrl}
      sx={{ 
        width: size, 
        height: size, 
        fontSize: `${size * 0.4}px`,
        backgroundColor: 'grey.400',
        color: 'white',
        fontWeight: 500,
        ...sx
      }}
    >
      {getInitials()}
    </Avatar>
  );
};

const CalendarView = ({ events, onEventClick, onDateClick, onCreateEvent, onDeleteEvent, timeFormat = '12hour', currentView = 'month', users = [], currentUser = null, canEdit = true, canDelete = true }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { companyId } = useParams();
  const [currentDate, setCurrentDate] = useState(new Date());
  
  // Quick add state
  const [quickAddOpen, setQuickAddOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [quickFormData, setQuickFormData] = useState({
    title: '',
    description: '',
    start_date: null,
    end_date: null,
    all_day: false,
    location: '',
    videocall_url: '',
    meeting_password: '',
    privacy: 'company',
    status: 'confirmed',
    is_recurring: false,
    recurrence_pattern: 'weekly',
    recurrence_end_date: null
  });
  const [quickAttendees, setQuickAttendees] = useState([]);
  
  // Day events popover state
  const [dayEventsOpen, setDayEventsOpen] = useState(false);
  const [dayEventsAnchor, setDayEventsAnchor] = useState(null);
  const [selectedDayEvents, setSelectedDayEvents] = useState([]);
  
  // Event view dialog state
  const [eventViewOpen, setEventViewOpen] = useState(false);
  const [viewingEvent, setViewingEvent] = useState(null);
  
  // Bulk delete state
  const [selectedEventsForDelete, setSelectedEventsForDelete] = useState([]);
  const [bulkDeleteMode, setBulkDeleteMode] = useState(false);

  const navigateNext = () => {
    switch (currentView) {
      case 'day':
        setCurrentDate(addDays(currentDate, 1));
        break;
      case 'week':
        setCurrentDate(addWeeks(currentDate, 1));
        break;
      case 'month':
        setCurrentDate(addMonths(currentDate, 1));
        break;
      case 'year':
        setCurrentDate(addYears(currentDate, 1));
        break;
    }
  };

  const navigatePrev = () => {
    switch (currentView) {
      case 'day':
        setCurrentDate(addDays(currentDate, -1));
        break;
      case 'week':
        setCurrentDate(addWeeks(currentDate, -1));
        break;
      case 'month':
        setCurrentDate(subMonths(currentDate, 1));
        break;
      case 'year':
        setCurrentDate(subYears(currentDate, 1));
        break;
    }
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const handleQuickAddOpen = (date) => {   
    setSelectedDate(date);
    
    // Check if this is a specific time slot (day/week view) or just a date (month/year view)
    const isSpecificTime = currentView === 'day' || currentView === 'week';
    let startDate, endDate;
    
    if (isSpecificTime && date instanceof Date) {
      // Use the exact time from day/week view
      startDate = dayjs(date);
      endDate = dayjs(date).add(1, 'hour');
    } else {
      // Default to start of day for month/year view
      startDate = dayjs(date).startOf('day');
      endDate = startDate;
    }
    
    setQuickFormData({
      title: '',
      description: '',
      start_date: startDate,
      end_date: endDate,
      all_day: false,
      location: '',
      videocall_url: '',
      meeting_password: '',
      privacy: 'company',
      status: 'confirmed',
      is_recurring: false,
      recurrence_pattern: 'weekly',
      recurrence_end_date: null
    });
    
    // Robust validation for currentUser ID - prevent undefined UUIDs
    if (currentUser && currentUser.id && typeof currentUser.id === 'string' && 
        currentUser.id !== 'undefined' && currentUser.id.length > 30) {
      setQuickAttendees([{
        id: currentUser.id,
        user_id: currentUser.id,
        is_organizer: true,
        status: 'accepted',
        user: {
          id: currentUser.id,
          full_name: currentUser.full_name,
          email: currentUser.email
        }
      }]);
    } else {
      console.warn('Invalid or missing currentUser ID, setting empty attendees:', currentUser);
      setQuickAttendees([]);
    }
    setQuickAddOpen(true);
  };

  const handleQuickInputChange = (field, value) => {
    if (field === 'all_day') {
      if (value) {
        // When turning on all_day, set times to start/end of day
        setQuickFormData(prev => ({
          ...prev,
          [field]: value,
          start_date: prev.start_date ? prev.start_date.startOf('day') : null,
          end_date: prev.end_date ? prev.end_date.endOf('day') : null
        }));
      } else {
        // When turning off all_day, keep the dates but clear times (user will set them)
        setQuickFormData(prev => ({
          ...prev,
          [field]: value,
          start_date: prev.start_date ? prev.start_date.startOf('day') : null,
          end_date: prev.end_date ? prev.end_date.startOf('day') : null
        }));
      }
    } else {
      setQuickFormData(prev => ({ ...prev, [field]: value }));
    }
  };

  const handleAddAttendee = (userId) => { 
    const user = users.find(u => u.id === userId);
    
    // Robust validation for userId - prevent undefined UUIDs
    if (user && userId && typeof userId === 'string' && userId !== 'undefined' && 
        userId.length > 30 && !quickAttendees.find(a => a.user_id === userId)) {
      const newAttendee = {
        id: userId,
        user_id: userId,
        is_organizer: false,
        status: 'pending',
        user: {
          id: userId,
          full_name: user.full_name,
          email: user.email
        }
      };
      setQuickAttendees(prev => [...prev, newAttendee]);
    } else {
      console.warn('Invalid userId or user not found, skipping attendee:', userId, user);
    }
  };

  const handleRemoveAttendee = (userId) => {
    // Don't allow removing the organizer
    if (quickAttendees.find(a => a.user_id === userId && a.is_organizer)) {
      return;
    }
    setQuickAttendees(prev => prev.filter(a => a.user_id !== userId));
  };

  const handleMoreOptions = () => {
    // Prepare full event data for EventEditor and pass via navigation state
    const fullEventData = {
      title: quickFormData.title.trim(),
      description: quickFormData.description || '',
      start_date: quickFormData.start_date ? quickFormData.start_date.toISOString() : null,
      end_date: quickFormData.end_date ? quickFormData.end_date.toISOString() : null,
      all_day: quickFormData.all_day,
      location: quickFormData.location || null,
      videocall_url: quickFormData.videocall_url || null,
      meeting_password: quickFormData.meeting_password || null,
      privacy: quickFormData.privacy,
      status: quickFormData.status,
      is_recurring: quickFormData.is_recurring,
      recurrence_pattern: quickFormData.is_recurring ? quickFormData.recurrence_pattern : null,
      recurrence_end_date: quickFormData.is_recurring && quickFormData.recurrence_end_date ? quickFormData.recurrence_end_date.toISOString() : null,
      attendees: quickAttendees
    };
    
    // Close quick add and navigate to full editor with pre-filled data
    handleQuickAddClose();
    navigate(`/company/${companyId}/calendar/edit/new`, {
      state: { prefilledEventData: fullEventData }
    });
  };

  const handleDayClick = (date, event) => {
    let relevantEvents;
    
    if (currentView === 'day') {
      // In day view, only show events for that specific hour
      relevantEvents = events.filter(evt => 
        evt.start_date && 
        isSameDay(new Date(evt.start_date), date) &&
        getHours(new Date(evt.start_date)) === getHours(date)
      );
    } else {
      // In other views, show all events for that day
      relevantEvents = events.filter(evt => 
        evt.start_date && isSameDay(new Date(evt.start_date), date)
      );
    }
    
    if (relevantEvents.length > 0) {
      setSelectedDayEvents(relevantEvents);
      setSelectedDate(date);
      setDayEventsAnchor(event.currentTarget);
      setDayEventsOpen(true);
    } else if (canEdit) {
      handleQuickAddOpen(date);
    }
  };

  const handleDayEventsClose = () => {
    setDayEventsOpen(false);
    setDayEventsAnchor(null);
    setSelectedDayEvents([]);
    setBulkDeleteMode(false);
    setSelectedEventsForDelete([]);
  };

  const handleCreateFromPopover = () => {
    if (!canEdit) return;
    handleDayEventsClose();
    handleQuickAddOpen(selectedDate);
  };

  const handleEventView = (event) => {
    setViewingEvent(event);
    setEventViewOpen(true);
  };

  const handleEventViewClose = () => {
    setEventViewOpen(false);
    setViewingEvent(null);
  };

  const handleEventEdit = () => {
    if (viewingEvent && canEdit) {
      handleEventViewClose();
      onEventClick(viewingEvent);
    }
  };

  const handleEventDelete = () => {
    if (viewingEvent && viewingEvent.id && onDeleteEvent && canDelete) {
      onDeleteEvent(viewingEvent.id);
      handleEventViewClose();
    }
  };

  const toggleBulkDeleteMode = () => {
    setBulkDeleteMode(!bulkDeleteMode);
    setSelectedEventsForDelete([]);
  };

  const toggleEventSelection = (eventId) => {
    setSelectedEventsForDelete(prev => 
      prev.includes(eventId) 
        ? prev.filter(id => id !== eventId)
        : [...prev, eventId]
    );
  };

  const selectAllEvents = () => {
    if (selectedEventsForDelete.length === selectedDayEvents.length) {
      setSelectedEventsForDelete([]);
    } else {
      setSelectedEventsForDelete(selectedDayEvents.map(event => event.id));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedEventsForDelete.length > 0 && onDeleteEvent && canDelete) {
      try {
        for (const eventId of selectedEventsForDelete) {
          await onDeleteEvent(eventId);
        }
        setBulkDeleteMode(false);
        setSelectedEventsForDelete([]);
        handleDayEventsClose();
      } catch (error) {
        console.error('Error deleting events:', error);
      }
    }
  };

  const getEventColor = (event, eventList = []) => {
    if (event.status === 'cancelled') return eventColors.cancelled;
    if (event.status === 'tentative') return eventColors.tentative;
    if (event.status === 'confirmed') return eventColors.confirmed;
    
    // Group events by time period based on current view
    let groupKey;
    const eventDate = new Date(event.start_date);
    
    switch (currentView) {
      case 'day':
        groupKey = format(eventDate, 'yyyy-MM-dd-HH');
        break;
      case 'week':
        groupKey = format(eventDate, 'yyyy-MM-dd');
        break;
      case 'month':
        groupKey = format(eventDate, 'yyyy-ww');
        break;
      case 'year':
        groupKey = format(eventDate, 'yyyy-MM');
        break;
      default:
        groupKey = format(eventDate, 'yyyy-MM-dd');
    }
    
    // Find all events in the same time group
    const groupEvents = eventList.filter(e => {
      if (!e.start_date) return false;
      const eDate = new Date(e.start_date);
      let eGroupKey;
      
      switch (currentView) {
        case 'day':
          eGroupKey = format(eDate, 'yyyy-MM-dd-HH');
          break;
        case 'week':
          eGroupKey = format(eDate, 'yyyy-MM-dd');
          break;
        case 'month':
          eGroupKey = format(eDate, 'yyyy-ww');
          break;
        case 'year':
          eGroupKey = format(eDate, 'yyyy-MM');
          break;
        default:
          eGroupKey = format(eDate, 'yyyy-MM-dd');
      }
      
      return eGroupKey === groupKey;
    });
    
    // Sort group events by start time to ensure consistent color assignment
    groupEvents.sort((a, b) => new Date(a.start_date) - new Date(b.start_date));
    
    // Find this event's index in the group
    const eventIndex = groupEvents.findIndex(e => e.id === event.id);
    
    // Return color based on index in group
    return dynamicColors[eventIndex % dynamicColors.length];
  };

  const handleQuickAdd = () => {   
    if (!quickFormData.title.trim()) return;
    
    // Validate date range
    if (quickFormData.start_date && quickFormData.end_date) {
      const startTime = new Date(quickFormData.start_date);
      const endTime = new Date(quickFormData.end_date);
      
      if (endTime <= startTime) {
        alert('Invalid date range: End date must be after start date');
        return;
      }
    }
    
    // Validate companyId before proceeding
    if (!companyId || companyId === 'undefined' || typeof companyId !== 'string' || companyId.length < 30) {
      console.error('Invalid companyId in CalendarView handleQuickAdd:', companyId);
      return;
    }
    
    // Only include attendees if they have valid user_id with robust validation
    const validAttendees = quickAttendees.filter(attendee => {
      if (!attendee || !attendee.user_id || typeof attendee.user_id !== 'string' || 
          attendee.user_id === 'undefined' || attendee.user_id.length < 30) {
        console.warn('Filtering out invalid attendee in QuickAdd:', attendee);
        return false;
      }
      return true;
    });
    
    // Check each attendee individually for debugging
    quickAttendees.forEach((attendee, index) => {
      if (!attendee || !attendee.user_id || attendee.user_id === 'undefined' || attendee.user_id.length < 30) {
        console.error(`BAD ATTENDEE ${index}:`, attendee);
      }
    });
    
    const quickEventData = {
      title: quickFormData.title.trim(),
      description: quickFormData.description || '',
      start_date: quickFormData.start_date ? quickFormData.start_date.toISOString() : null,
      end_date: quickFormData.end_date ? quickFormData.end_date.toISOString() : null,
      all_day: quickFormData.all_day,
      location: quickFormData.location || null,
      videocall_url: quickFormData.videocall_url || null,
      meeting_password: quickFormData.meeting_password || null,
      privacy: quickFormData.privacy,
      status: quickFormData.status,
      is_recurring: quickFormData.is_recurring,
      recurrence_pattern: quickFormData.is_recurring ? quickFormData.recurrence_pattern : null,
      recurrence_end_date: quickFormData.is_recurring && quickFormData.recurrence_end_date ? quickFormData.recurrence_end_date.toISOString() : null,
      attendees: validAttendees
    };
    
    // Remove any fields that might have undefined values
    Object.keys(quickEventData).forEach(key => {
      if (quickEventData[key] === undefined || quickEventData[key] === "undefined") {
        delete quickEventData[key];
      }
    });
    
    onDateClick(quickEventData);
    
    handleQuickAddClose();
  };

  const handleQuickAddClose = () => {
    setQuickAddOpen(false);
    setQuickFormData({
      title: '',
      description: '',
      start_date: null,
      end_date: null,
      all_day: false,
      location: '',
      videocall_url: '',
      meeting_password: '',
      privacy: 'company',
      status: 'confirmed',
      is_recurring: false,
      recurrence_pattern: 'weekly',
      recurrence_end_date: null
    });
    setQuickAttendees([]);
    setSelectedDate(null);
  };

  const getViewTitle = () => {
    switch (currentView) {
      case 'day':
        return format(currentDate, 'EEEE, MMMM d, yyyy');
      case 'week':
        const weekStart = startOfWeek(currentDate);
        const weekEnd = endOfWeek(currentDate);
        return `${format(weekStart, 'MMM d')} - ${format(weekEnd, 'MMM d, yyyy')}`;
      case 'month':
        return format(currentDate, 'MMMM yyyy');
      case 'year':
        return format(currentDate, 'yyyy');
      default:
        return format(currentDate, 'MMMM yyyy');
    }
  };

  const renderCalendarContent = () => {
    switch (currentView) {
      case 'day':
        return renderDayView();
      case 'week':
        return renderWeekView();
      case 'month':
        return renderMonthView();
      case 'year':
        return renderYearView();
      default:
        return renderMonthView();
    }
  };

  const renderDayView = () => {
    const dayStart = startOfDay(currentDate);
    const dayEnd = endOfDay(currentDate);
    const hours = eachHourOfInterval({ start: dayStart, end: dayEnd });
    
    const dayEvents = events.filter(event => 
      event.start_date && isSameDay(new Date(event.start_date), currentDate)
    );
    
    // Separate all-day and timed events
    const allDayEvents = dayEvents.filter(event => event.all_day);
    const timedEvents = dayEvents.filter(event => !event.all_day);

    return (
      <Box sx={{ p: 0 }}>
        {/* All-day events section */}
        {allDayEvents.length > 0 && (
          <Box sx={{ 
            p: 2, 
            borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`,
            backgroundColor: alpha(theme.palette.grey[50], 0.3)
          }}>
            <Typography variant="caption" sx={{ fontWeight: 500, color: 'text.secondary', mb: 1, display: 'block' }}>
              All Day
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {allDayEvents.map((event) => (
                <Chip
                  key={event.id}
                  label={event.title}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEventView(event);
                  }}
                  sx={{
                    height: 28,
                    fontSize: '0.8rem',
                    fontWeight: 500,
                    backgroundColor: getEventColor(event, events),
                    color: 'white',
                    borderRadius: 2,
                    cursor: 'pointer',
                    '&:hover': {
                      opacity: 0.8
                    }
                  }}
                />
              ))}
            </Box>
          </Box>
        )}
        
        <Box sx={{ display: 'grid', gridTemplateColumns: '80px 1fr', maxHeight: '75vh', overflow: 'auto' }} data-calendar-container>
          {/* Time column */}
          <Box sx={{ pt: 1 }}>
            {hours.map((hour) => (
              <Box
                key={hour}
                sx={{
                  height: 60,
                  borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.75rem',
                  color: 'text.secondary'
                }}
              >
                {format(hour, timeFormat === '24hour' ? 'HH:mm' : 'h:mm a')}
              </Box>
            ))}
          </Box>
          
          {/* Events column */}
          <Box sx={{ position: 'relative', pt: 1 }}>
            {hours.map((hour) => (
              <Box
                key={hour}
                sx={{
                  height: 60,
                  borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`,
                  borderLeft: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`,
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.05)
                  }
                }}
                onClick={(e) => handleDayClick(hour, e)}
              />
            ))}
            
            {/* Render events */}
            {(() => {
              const eventsByHour = {};
              timedEvents.forEach((event, originalIndex) => {
                const eventHour = getHours(new Date(event.start_date));
                if (!eventsByHour[eventHour]) eventsByHour[eventHour] = [];
                eventsByHour[eventHour].push({...event, originalIndex});
              });

              return Object.entries(eventsByHour)
                .sort(([a], [b]) => parseInt(a) - parseInt(b))
                .map(([hour, hourEvents]) => {
                  // Sort events within each hour by their original order
                  hourEvents.sort((a, b) => a.originalIndex - b.originalIndex);
                  
                const top = (parseInt(hour) * 60) + 8;
                // Calculate actual available width based on the calendar container
                const containerElement = document.querySelector('[data-calendar-container]');
                let availableWidth;
                if (containerElement) {
                  const containerRect = containerElement.getBoundingClientRect();
                  availableWidth = Math.max(containerRect.width - 120, 400);
                } else {
                  // Fallback if container not found
                  availableWidth = Math.max(window.innerWidth - 200, 400);
                }
                
                let totalWidth = 0;
                let maxVisible = 0;
                
                // Calculate how many events can fit
                for (let i = 0; i < hourEvents.length; i++) {
                  const eventWidth = Math.min(Math.max(hourEvents[i].title.length * 7 + 40, 120), 250);
                  const gapWidth = i > 0 ? 6 : 0;
                  const needsPlusButton = i < hourEvents.length - 1; // Not the last event
                  const plusButtonWidth = needsPlusButton ? 76 : 0; // 60px + 6px gap + 10px margin
                  
                  if (totalWidth + eventWidth + gapWidth + plusButtonWidth <= availableWidth) {
                    totalWidth += eventWidth + gapWidth;
                    maxVisible++;
                  } else {
                    // If we can't fit this event, try to fit at least the + button
                    if (maxVisible > 0 && totalWidth + 76 <= availableWidth) {
                      break; // Show + button
                    } else if (maxVisible === 0) {
                      maxVisible = 1; // Show at least one event, even if it gets cut off
                    }
                    break;
                  }
                }
                
                // If we fit all events, no need for + button
                if (maxVisible >= hourEvents.length) {
                  maxVisible = hourEvents.length;
                }
                
                const visibleEvents = hourEvents.slice(0, maxVisible);
                const hiddenEvents = hourEvents.slice(maxVisible);
                const hiddenCount = hiddenEvents.length;

                return (
                  <Box key={hour} sx={{ position: 'absolute', top: `${top}px`, left: 8, right: 8, height: 60, pointerEvents: 'none' }}>
                    {visibleEvents.map((event, index) => {
                      const eventStart = new Date(event.start_date);
                      const eventEnd = new Date(event.end_date);
                      const duration = (eventEnd - eventStart) / (1000 * 60 * 60); // Hours
                      const eventHeight = Math.max(duration * 60 - 4, 50);
                      const eventWidth = Math.min(Math.max(event.title.length * 7 + 40, 120), 250);
                      let leftPosition = 0;
                      
                      // Calculate position to avoid overlap
                      for (let i = 0; i < index; i++) {
                        const prevEventWidth = Math.min(Math.max(visibleEvents[i].title.length * 7 + 40, 120), 250);
                        leftPosition += prevEventWidth + 6;
                      }
                      
                      return (
                        <Box
                          key={event.id}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEventView(event);
                          }}
                          sx={{
                            position: 'absolute',
                            top: 0,
                            left: `${leftPosition}px`,
                            width: `${eventWidth}px`,
                            height: `${eventHeight}px`,
                            backgroundColor: alpha(getEventColor(event, events), 0.9),
                            color: 'white',
                            borderRadius: 2,
                            p: 1,
                            cursor: 'pointer',
                            fontSize: '0.8rem',
                            fontWeight: 500,
                            zIndex: 1,
                            overflow: 'hidden',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 0.5,
                            pointerEvents: 'auto'
                          }}
                        >
                          <Typography variant="subtitle2" sx={{ fontSize: '0.85rem', fontWeight: 600 }}>
                            {event.title}
                          </Typography>
                          {!event.all_day && (
                            <Typography variant="caption" sx={{ fontSize: '0.7rem', opacity: 0.9 }}>
                              {format(eventStart, timeFormat === '24hour' ? 'HH:mm' : 'h:mm a')} - {format(eventEnd, timeFormat === '24hour' ? 'HH:mm' : 'h:mm a')}
                            </Typography>
                          )}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 'auto' }}>
                            {event.attendees?.length > 0 && (
                              <GroupIcon sx={{ fontSize: 12, opacity: 0.8 }} />
                            )}
                            {event.videocall_url && (
                              <VideoCallIcon sx={{ fontSize: 12, opacity: 0.8 }} />
                            )}
                            {event.location && (
                              <LocationIcon sx={{ fontSize: 12, opacity: 0.8 }} />
                            )}
                          </Box>
                        </Box>
                      );
                    })}
                    
                    {hiddenCount > 0 && (() => {
                      // Calculate where to place the + button (after the last visible event)
                      let buttonLeftPosition = 0;
                      for (let i = 0; i < visibleEvents.length; i++) {
                        const eventWidth = Math.min(Math.max(visibleEvents[i].title.length * 7 + 40, 120), 250);
                        buttonLeftPosition += eventWidth + (i > 0 ? 6 : 0);
                      }
                      if (visibleEvents.length > 0) {
                        buttonLeftPosition += 6; // Gap before + button
                      }
                      
                      return (
                        <Box
                                                      onClick={(e) => {
                              e.stopPropagation();
                              // Show only the hidden events in the popup
                              setSelectedDayEvents(hiddenEvents);
                              setSelectedDate(currentDate);
                              setDayEventsAnchor(e.currentTarget);
                              setDayEventsOpen(true);
                            }}
                          sx={{
                            position: 'absolute',
                            top: 0,
                            left: `${buttonLeftPosition}px`,
                            width: 60,
                            height: 50,
                            backgroundColor: alpha(theme.palette.grey[500], 0.9),
                            color: 'white',
                            borderRadius: 2,
                            cursor: 'pointer',
                            fontSize: '0.7rem',
                            fontWeight: 500,
                            zIndex: 10,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            border: `1px solid ${alpha(theme.palette.grey[300], 0.3)}`,
                            pointerEvents: 'auto'
                          }}
                        >
                          +{hiddenCount}
                        </Box>
                      );
                    })()}
                  </Box>
                );
              });
            })()}
          </Box>
        </Box>
      </Box>
    );
  };

  const renderWeekView = () => {
    const weekStart = startOfWeek(currentDate);
    const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
    const hours = eachHourOfInterval({ 
      start: setHours(weekStart, 0), 
      end: setHours(weekStart, 23) 
    });
    
    // Get all events for the week
    const weekEvents = events.filter(event => 
      event.start_date && 
      new Date(event.start_date) >= weekStart && 
      new Date(event.start_date) <= endOfWeek(currentDate)
    );
    
    // Separate all-day and timed events for the week
    const weekAllDayEvents = weekEvents.filter(event => event.all_day);
    const weekTimedEvents = weekEvents.filter(event => !event.all_day);

    return (
      <Box sx={{ p: 0 }}>
        {/* All-day events section for week */}
        {weekAllDayEvents.length > 0 && (
          <Box sx={{ 
            p: 2, 
            borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`,
            backgroundColor: alpha(theme.palette.grey[50], 0.3)
          }}>
            <Typography variant="caption" sx={{ fontWeight: 500, color: 'text.secondary', mb: 1, display: 'block' }}>
              All Day Events This Week
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {weekAllDayEvents.map((event) => (
                <Chip
                  key={event.id}
                  label={`${event.title} - ${format(new Date(event.start_date), 'MMM d')}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEventView(event);
                  }}
                  sx={{
                    height: 28,
                    fontSize: '0.8rem',
                    fontWeight: 500,
                    backgroundColor: getEventColor(event, events),
                    color: 'white',
                    borderRadius: 2,
                    cursor: 'pointer',
                    '&:hover': {
                      opacity: 0.8
                    }
                  }}
                />
              ))}
            </Box>
                     </Box>
         )}
        
        {/* Week header */}
        <Box sx={{ display: 'grid', gridTemplateColumns: '80px repeat(7, 1fr)', borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.5)}` }}>
          <Box />
          {weekDays.map((day) => (
            <Box
              key={day}
              sx={{
                p: 1.5,
                textAlign: 'center',
                borderLeft: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`,
                backgroundColor: isToday(day) ? alpha(theme.palette.primary.main, 0.1) : 'transparent'
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 500, color: isToday(day) ? 'primary.main' : 'text.primary' }}>
                {format(day, 'EEE')}
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 500, color: isToday(day) ? 'primary.main' : 'text.primary' }}>
                {format(day, 'd')}
              </Typography>
            </Box>
          ))}
        </Box>
        
        {/* Week grid */}
        <Box sx={{ maxHeight: '70vh', overflow: 'auto' }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: '80px repeat(7, 1fr)' }}>
            {/* Time column */}
            <Box sx={{ pt: 1 }}>
              {hours.map((hour) => (
                <Box
                  key={hour}
                  sx={{
                    height: 50,
                    borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.75rem',
                    color: 'text.secondary'
                  }}
                >
                  {format(hour, timeFormat === '24hour' ? 'HH:mm' : 'h:mm a')}
                </Box>
              ))}
            </Box>
            
            {/* Day columns */}
            {weekDays.map((day) => (
              <Box key={day} sx={{ position: 'relative', pt: 1 }}>
                {hours.map((hour) => (
                  <Box
                    key={hour}
                    sx={{
                      height: 50,
                      borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`,
                      borderLeft: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`,
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.05)
                      }
                    }}
                    onClick={(e) => handleDayClick(setHours(day, getHours(hour)), e)}
                  />
                ))}
                
                {/* Render events for this day */}
                {(() => {
                  const dayTimedEvents = weekTimedEvents.filter(event => isSameDay(new Date(event.start_date), day));
                  const eventsByHour = {};
                  dayTimedEvents.forEach((event, originalIndex) => {
                    const eventHour = getHours(new Date(event.start_date));
                    if (!eventsByHour[eventHour]) eventsByHour[eventHour] = [];
                    eventsByHour[eventHour].push({...event, originalIndex});
                  });

                  return Object.entries(eventsByHour)
                    .sort(([a], [b]) => parseInt(a) - parseInt(b))
                    .map(([hour, hourEvents]) => {
                      // Sort events within each hour by their original order
                      hourEvents.sort((a, b) => a.originalIndex - b.originalIndex);
                    const top = (parseInt(hour) * 50) + 8;
                    const maxVisible = 1; // Limit to 1 event per hour slot in week view
                    const visibleEvents = hourEvents.slice(0, maxVisible);
                    const hiddenEvents = hourEvents.slice(maxVisible);
                    const hiddenCount = hiddenEvents.length;

                                          return (
                        <Box key={`${day}-${hour}`} sx={{ position: 'absolute', top: `${top}px`, left: 2, right: 2, height: 50, pointerEvents: 'none' }}>
                          {visibleEvents.map((event, index) => (
                          <Box
                            key={event.id}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEventView(event);
                            }}
                            sx={{
                              position: 'absolute',
                              top: index * 35,
                              left: 0,
                              right: hiddenCount > 0 && index === maxVisible - 1 ? 50 : 0,
                              height: 32,
                              backgroundColor: getEventColor(event, events),
                              color: 'white',
                              borderRadius: 2,
                              px: 1.5,
                              py: 1,
                              cursor: 'pointer',
                              fontSize: '0.75rem',
                              fontWeight: 500,
                              zIndex: 1,
                              overflow: 'hidden',
                              display: 'flex',
                              alignItems: 'center',
                              pointerEvents: 'auto'
                            }}
                          >
                            <Box sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {event.title}
                            </Box>
                          </Box>
                        ))}
                        
                        {hiddenCount > 0 && (
                          <Box
                            onClick={(e) => {
                              e.stopPropagation();
                              // Show all timed events for this hour (excluding all-day events)
                              const hourEvents = weekTimedEvents.filter(evt => 
                                isSameDay(new Date(evt.start_date), day) &&
                                getHours(new Date(evt.start_date)) === parseInt(hour)
                              );
                              setSelectedDayEvents(hourEvents);
                              setSelectedDate(day);
                              setDayEventsAnchor(e.currentTarget);
                              setDayEventsOpen(true);
                            }}
                            sx={{
                              position: 'absolute',
                              top: (maxVisible - 1) * 35,
                              right: 0,
                              width: 45,
                              height: 32,
                              backgroundColor: alpha(theme.palette.grey[600], 0.8),
                              color: 'white',
                              borderRadius: 2,
                              cursor: 'pointer',
                              fontSize: '0.7rem',
                              fontWeight: 500,
                              zIndex: 2,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              pointerEvents: 'auto'
                            }}
                          >
                            +{hiddenCount}
                          </Box>
                        )}
                      </Box>
                    );
                  });
                })()}
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    );
  };

  const renderYearView = () => {
    const months = Array.from({ length: 12 }, (_, i) => new Date(currentDate.getFullYear(), i, 1));

    return (
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 3, width: '100%' }}>
          {months.map((month) => {
            const monthStart = startOfMonth(month);
            const monthEnd = endOfMonth(monthStart);
            const startDate = startOfWeek(monthStart);
            const endDate = endOfWeek(monthEnd);
            
            const monthEvents = events.filter(event => 
              event.start_date && 
              new Date(event.start_date) >= monthStart && 
              new Date(event.start_date) <= monthEnd
            );

            const weeks = [];
            let day = startDate;
            
            while (day <= endDate) {
              const week = [];
              for (let i = 0; i < 7; i++) {
                week.push(day);
                day = addDays(day, 1);
              }
              weeks.push(week);
            }

            return (
              <Box
                key={month}
                sx={{
                  border: `1px solid ${alpha(theme.palette.grey[300], 0.3)}`,
                  borderRadius: 2,
                  overflow: 'hidden',
                  backgroundColor: 'white',
                  aspectRatio: '1',
                  display: 'flex',
                  flexDirection: 'column',
                  boxShadow: `0 1px 3px ${alpha(theme.palette.grey[400], 0.1)}`
                }}
              >
                <Box sx={{ 
                  py: 1.5, 
                  px: 1,
                  backgroundColor: alpha(theme.palette.grey[50], 0.8),
                  borderBottom: `1px solid ${alpha(theme.palette.grey[200], 0.8)}`,
                  minHeight: 'auto'
                }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, textAlign: 'center', color: 'text.primary', fontSize: '0.875rem' }}>
                    {format(month, 'MMM')}
                  </Typography>
                </Box>
                
                <Box sx={{ p: 1.5, flex: 1, display: 'flex', flexDirection: 'column' }}>
                  {/* Days header */}
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', mb: 0.5, py: 0.5 }}>
                    {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((dayName, index) => (
                      <Typography key={index} variant="caption" sx={{ textAlign: 'center', fontSize: '0.7rem', color: 'text.secondary', lineHeight: 1.2, fontWeight: 500 }}>
                        {dayName}
                      </Typography>
                    ))}
                  </Box>
                  
                  {/* Calendar grid */}
                  <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                    {weeks.map((week, weekIndex) => (
                      <Box key={weekIndex} sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', flex: 1, mb: 0.25 }}>
                        {week.map((day) => {
                          const dayEvents = monthEvents.filter(event => isSameDay(new Date(event.start_date), day));
                          
                          return (
                            <Box
                              key={day}
                              sx={{
                                minHeight: 24,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                borderRadius: 1,
                                mx: 0.25,
                                my: 0.125,
                                backgroundColor: !isSameMonth(day, month) 
                                  ? 'transparent'
                                  : isToday(day) 
                                    ? alpha(theme.palette.primary.main, 0.15)
                                    : dayEvents.length > 0
                                      ? alpha(getEventColor(dayEvents[0], events), 0.25)
                                      : 'transparent',
                                '&:hover': {
                                  backgroundColor: !isSameMonth(day, month) 
                                    ? alpha(theme.palette.grey[300], 0.1)
                                    : alpha(theme.palette.primary.main, 0.08)
                                }
                              }}
                              onClick={(e) => handleDayClick(day, e)}
                            >
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  fontSize: '0.7rem',
                                  fontWeight: isToday(day) ? 600 : 400,
                                  color: !isSameMonth(day, month) 
                                    ? alpha(theme.palette.text.disabled, 0.6)
                                    : isToday(day) 
                                      ? 'primary.main' 
                                      : 'text.primary',
                                  lineHeight: 1.2
                                }}
                              >
                                {format(day, 'd')}
                              </Typography>
                            </Box>
                          );
                        })}
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    );
  };

  const renderMonthView = () => {
    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(monthStart);
    const startDate = startOfWeek(monthStart);
    const endDate = endOfWeek(monthEnd);

    const dateFormat = "d";
    const rows = [];
    let days = [];
    let day = startDate;
    let formattedDate = "";

    // Generate calendar grid
    while (day <= endDate) {
      for (let i = 0; i < 7; i++) {
        formattedDate = format(day, dateFormat);
        const cloneDay = day;
        
        // Get events for this day
        const dayEvents = events.filter(event => 
          event.start_date && isSameDay(new Date(event.start_date), day)
        );

        days.push(
          <Box
            key={day}
            sx={{
              minHeight: 120,
              border: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`,
              p: 1,
              cursor: 'pointer',
              backgroundColor: !isSameMonth(day, monthStart) 
                ? alpha(theme.palette.grey[100], 0.5)
                : isToday(day) 
                  ? alpha(theme.palette.primary.main, 0.08)
                  : 'background.paper',
              transition: 'all 0.2s ease',
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.1)
              }
            }}
            onClick={(e) => handleDayClick(cloneDay, e)}
          >
            <Typography
              variant="body2"
              sx={{
                fontWeight: isToday(day) ? 500 : 500,
                color: !isSameMonth(day, monthStart) 
                  ? 'text.disabled' 
                  : isToday(day) 
                    ? 'primary.main' 
                    : 'text.primary',
                mb: 1,
                fontSize: '0.9rem'
              }}
            >
              {formattedDate}
            </Typography>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {dayEvents.slice(0, 2).map((event) => (
                <Tooltip key={event.id} title={`${event.title} ${event.start_date ? format(new Date(event.start_date), timeFormat === '24hour' ? 'HH:mm' : 'h:mm a') : ''}`}>
                  <Chip
                    label={event.title}
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEventView(event);
                    }}
                    sx={{
                      height: 24,
                      fontSize: '0.7rem',
                      fontWeight: 500,
                      backgroundColor: getEventColor(event, events),
                      color: 'white',
                      borderRadius: 2,
                      cursor: 'pointer',
                      '& .MuiChip-label': {
                        px: 1,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        maxWidth: '100%'
                      },
                      '&:hover': {
                        backgroundColor: getEventColor(event, events),
                        opacity: 0.8
                      },
                      transition: 'all 0.2s ease'
                    }}
                  />
                </Tooltip>
              ))}
              
              {dayEvents.length > 2 && (
                <Chip
                  label={`+${dayEvents.length - 2} more`}
                  size="small"
                                      onClick={(e) => {
                    e.stopPropagation();
                    // Show all events for this day
                    setSelectedDayEvents(dayEvents);
                    setSelectedDate(cloneDay);
                    setDayEventsAnchor(e.currentTarget);
                    setDayEventsOpen(true);
                  }}
                  sx={{
                    height: 24,
                    fontSize: '0.7rem',
                    fontWeight: 500,
                    backgroundColor: alpha(theme.palette.grey[600], 0.8),
                    color: 'white',
                    borderRadius: 2,
                    cursor: 'pointer',
                    '& .MuiChip-label': {
                      px: 1
                    },
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.grey[600], 0.9)
                    },
                    transition: 'all 0.2s ease'
                  }}
                />
              )}
            </Box>
          </Box>
        );
        day = addDays(day, 1);
      }
      rows.push(
        <Box key={day} sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}>
          {days}
        </Box>
      );
      days = [];
    }

    return (
      <Box sx={{ p: 0 }}>
        {rows}
      </Box>
    );
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <CalendarContainer data-calendar-container>
        {/* Calendar Header */}
        <Box sx={{ 
          p: 3, 
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.02)})`,
          borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h5" sx={{ 
              fontWeight: 500,
              color: 'primary.main',
              fontSize: '1.5rem'
            }}>
              {getViewTitle()}
            </Typography>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Button
                variant="contained"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => onCreateEvent()}
                disabled={!canEdit}
                sx={{
                  borderRadius: 2,
                  fontWeight: 500,
                  px: 3,
                  boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                  '&:hover': {
                    boxShadow: `0 6px 16px ${alpha(theme.palette.primary.main, 0.4)}`
                  },
                  '&:disabled': {
                    opacity: 0.6,
                    boxShadow: 'none'
                  },
                  transition: 'all 0.2s ease'
                }}
              >
                New Event
              </Button>

              <Button
                variant="outlined"
                size="small"
                startIcon={<TodayIcon />}
                onClick={goToToday}
                sx={{
                  borderRadius: 2,
                  fontWeight: 500,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                  backgroundColor: alpha(theme.palette.primary.main, 0.05),
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.1)
                  },
                  transition: 'all 0.2s ease'
                }}
              >
                Today
              </Button>

              
              <IconButton 
                onClick={navigatePrev}
                sx={{
                  borderRadius: 2,
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`,
                  backgroundColor: 'white',
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                    borderColor: theme.palette.primary.main
                  },
                  transition: 'all 0.2s ease'
                }}
              >
                <ChevronLeftIcon />
              </IconButton>
              
              <IconButton 
                onClick={navigateNext}
                sx={{
                  borderRadius: 2,
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`,
                  backgroundColor: 'white',
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                    borderColor: theme.palette.primary.main
                  },
                  transition: 'all 0.2s ease'
                }}
              >
                <ChevronRightIcon />
              </IconButton>
            </Box>
          </Box>

          {/* Days of Week Header - Only for Month View */}
          {currentView === 'month' && (
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}>
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                <Box
                  key={day}
                  sx={{
                    p: 1.5,
                    textAlign: 'center',
                    borderBottom: `2px solid ${theme.palette.primary.main}`,
                    backgroundColor: alpha(theme.palette.primary.main, 0.05)
                  }}
                >
                  <Typography variant="subtitle2" sx={{ 
                    fontWeight: 500, 
                    color: 'primary.main',
                    fontSize: '0.875rem'
                  }}>
                    {day}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </Box>

        {/* Calendar Content */}
        {renderCalendarContent()}

        {/* Legend */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          gap: 3, 
          p: 3,
          background: `linear-gradient(135deg, ${alpha(theme.palette.grey[50], 0.8)}, ${alpha(theme.palette.grey[100], 0.4)})`,
          borderTop: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`
        }}>
          {Object.entries(eventColors).map(([status, color]) => (
            <Box key={status} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                sx={{
                  width: 14,
                  height: 14,
                  backgroundColor: color,
                  borderRadius: '50%',
                  border: `2px solid ${alpha(color, 0.3)}`,
                  boxShadow: `0 2px 8px ${alpha(color, 0.3)}`
                }}
              />
              <Typography variant="caption" sx={{ 
                textTransform: 'capitalize',
                fontWeight: 500,
                color: 'text.secondary',
                fontSize: '0.8rem'
              }}>
                {status}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* Day Events Popover */}
        <Popover
          open={dayEventsOpen}
          anchorEl={dayEventsAnchor}
          onClose={handleDayEventsClose}
          disableScrollLock={true}
          disablePortal={false}
          disableAutoFocus={true}
          disableEnforceFocus={true}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
          PaperProps={{
            sx: {
              position: 'absolute !important',
              minWidth: 300,
              maxWidth: 400,
              borderRadius: 2,
              boxShadow: `0 8px 24px ${alpha(theme.palette.grey[900], 0.15)}`,
              border: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`,
              contain: 'layout style paint',
              zIndex: 9999
            }
          }}
          sx={{
            zIndex: 9999
          }}
        >
          <Box sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 500, color: 'text.primary' }}>
                {selectedDate && format(selectedDate, 'MMMM d, yyyy')}
              </Typography>
              
              {selectedDayEvents.length > 1 && canDelete && (
                <Button
                  onClick={toggleBulkDeleteMode}
                  variant={bulkDeleteMode ? "contained" : "outlined"}
                  size="small"
                  color={bulkDeleteMode ? "error" : "primary"}
                  sx={{ borderRadius: 2, fontWeight: 500, fontSize: '0.8rem' }}
                >
                  {bulkDeleteMode ? 'Cancel' : 'Bulk Delete'}
                </Button>
              )}
            </Box>
            
            {bulkDeleteMode && selectedDayEvents.length > 1 && (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, p: 1, backgroundColor: alpha(theme.palette.grey[100], 0.5), borderRadius: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Checkbox
                    checked={selectedEventsForDelete.length === selectedDayEvents.length}
                    indeterminate={selectedEventsForDelete.length > 0 && selectedEventsForDelete.length < selectedDayEvents.length}
                    onChange={selectAllEvents}
                    size="small"
                  />
                  <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.85rem' }}>
                    Select All ({selectedDayEvents.length})
                  </Typography>
                </Box>
                
                {selectedEventsForDelete.length > 0 && canDelete && (
                  <Button
                    onClick={handleBulkDelete}
                    variant="contained"
                    color="error"
                    size="small"
                    sx={{ borderRadius: 2, fontWeight: 500, fontSize: '0.8rem' }}
                  >
                    <DeleteIcon sx={{ mr: 0.5, fontSize: 16 }} />
                    Delete ({selectedEventsForDelete.length})
                  </Button>
                )}
              </Box>
            )}
            
            <List sx={{ py: 0 }}>
              {selectedDayEvents.map((event, index) => (
                <Box key={event.id}>
                  <ListItem 
                    sx={{ 
                      px: 0, 
                      py: 1,
                      cursor: 'pointer',
                      borderRadius: 1,
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.grey[100], 0.5)
                      }
                    }}
                    onClick={() => {
                      if (!bulkDeleteMode) {
                        handleDayEventsClose();
                        handleEventView(event);
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                      {bulkDeleteMode && (
                        <Checkbox
                          checked={selectedEventsForDelete.includes(event.id)}
                          onChange={() => toggleEventSelection(event.id)}
                          size="small"
                          onClick={(e) => e.stopPropagation()}
                        />
                      )}
                      
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          backgroundColor: getEventColor(event, events),
                          borderRadius: '50%',
                          flexShrink: 0
                        }}
                      />
                      <ListItemText 
                        primary={event.title}
                        secondary={event.start_date ? format(new Date(event.start_date), timeFormat === '24hour' ? 'HH:mm' : 'h:mm a') : ''}
                        primaryTypographyProps={{
                          sx: { fontSize: '0.9rem', fontWeight: 500 }
                        }}
                        secondaryTypographyProps={{
                          sx: { fontSize: '0.75rem', color: 'text.secondary' }
                        }}
                      />
                    </Box>
                  </ListItem>
                  {index < selectedDayEvents.length - 1 && <Divider sx={{ my: 0.5 }} />}
                </Box>
              ))}
            </List>
            
            <Button
              fullWidth
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleCreateFromPopover}
              disabled={!canEdit}
              sx={{
                mt: 2,
                borderRadius: 2,
                fontWeight: 500,
                color: 'primary.main',
                borderColor: alpha(theme.palette.primary.main, 0.5),
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.05),
                  borderColor: theme.palette.primary.main
                },
                '&:disabled': {
                  opacity: 0.6,
                  borderColor: alpha(theme.palette.grey[400], 0.5),
                  color: 'text.disabled'
                }
              }}
            >
              Create Event
            </Button>
          </Box>
        </Popover>

        {/* Event View Dialog */}
        <Dialog
          open={eventViewOpen}
          onClose={handleEventViewClose}
          maxWidth="md"
          fullWidth
          TransitionComponent={Fade}
          BackdropComponent={Backdrop}
          disableScrollLock={true}
          disablePortal={false}
          disableAutoFocus={true}
          disableEnforceFocus={true}
          BackdropProps={{
            timeout: 500,
            sx: { backgroundColor: alpha(theme.palette.grey[900], 0.4) }
          }}
          PaperProps={{
            sx: {
              position: 'absolute !important',
              borderRadius: 3,
              maxWidth: 600,
              mx: 'auto',
              contain: 'layout style paint',
              zIndex: 9999
            }
          }}
          sx={{
            zIndex: 9999
          }}
        >
          <DialogTitle sx={{ 
            fontWeight: 500, 
            color: 'primary.main',
            borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`,
            py: 3,
            fontSize: '1.25rem',
            display: 'flex',
            alignItems: 'center',
            gap: 2
          }}>
            <Box sx={{
              p: 1,
              borderRadius: 2,
              background: `linear-gradient(${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
            }}>
              <EventIcon sx={{ fontSize: 24, color: 'primary.main' }} />
            </Box>
            {viewingEvent?.title || 'Event Details'}
          </DialogTitle>
          <DialogContent sx={{ pt: 4, pb: 3, px: 4, mt: 2 }}>
            {viewingEvent && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {/* Event Status */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      backgroundColor: getEventColor(viewingEvent, events),
                      borderRadius: '50%',
                                              border: `2px solid ${alpha(getEventColor(viewingEvent, events), 0.3)}`
                    }}
                  />
                  <Typography variant="body2" sx={{ fontWeight: 500, textTransform: 'capitalize', color: 'text.primary' }}>
                    {viewingEvent.status}
                  </Typography>
                </Box>

                {/* Date and Time */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ScheduleIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {viewingEvent.start_date && format(new Date(viewingEvent.start_date), 'EEEE, MMMM d, yyyy')}
                  </Typography>
                </Box>
                
                {!viewingEvent.all_day && viewingEvent.start_date && (
                  <Box sx={{ pl: 3 }}>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      {format(new Date(viewingEvent.start_date), timeFormat === '24hour' ? 'HH:mm' : 'h:mm a')} - {viewingEvent.end_date && format(new Date(viewingEvent.end_date), timeFormat === '24hour' ? 'HH:mm' : 'h:mm a')}
                    </Typography>
                  </Box>
                )}

                {/* Description */}
                {viewingEvent.description && (
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 500, mb: 1, color: 'text.primary' }}>
                      Description
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', whiteSpace: 'pre-wrap' }}>
                      {viewingEvent.description}
                    </Typography>
                  </Box>
                )}

                {/* Location */}
                {viewingEvent.location && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LocationIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                    <Typography variant="body2" sx={{ color: 'text.primary' }}>
                      {viewingEvent.location}
                    </Typography>
                  </Box>
                )}

                {/* Video Call */}
                {viewingEvent.videocall_url && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <VideoCallIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                    <Typography 
                      variant="body2" 
                      onClick={(e) => {
                        e.preventDefault();
                        let url = viewingEvent.videocall_url;
                        if (!url.startsWith('http://') && !url.startsWith('https://')) {
                          url = 'https://' + url;
                        }
                        window.open(url, '_blank', 'noopener,noreferrer');
                      }}
                      sx={{ 
                        color: 'primary.main', 
                        textDecoration: 'none',
                        cursor: 'pointer',
                        '&:hover': { textDecoration: 'underline' }
                      }}
                    >
                      Join Video Call
                    </Typography>
                  </Box>
                )}

                {/* Meeting Password */}
                {viewingEvent.meeting_password && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LockIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                    <Typography variant="body2" sx={{ color: 'text.primary' }}>
                      Password: {viewingEvent.meeting_password}
                    </Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<ContentCopyIcon sx={{ fontSize: 14 }} />}
                      onClick={() => {
                        navigator.clipboard.writeText(viewingEvent.meeting_password);
                      }}
                      sx={{ 
                        ml: 1, 
                        fontSize: '0.75rem', 
                        py: 0.5, 
                        px: 1.5,
                        minWidth: 'auto',
                        borderRadius: 2,
                        fontWeight: 500
                      }}
                    >
                      Copy
                    </Button>
                  </Box>
                )}

                {/* Attendees */}
                {viewingEvent.attendees && viewingEvent.attendees.length > 0 && (
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <GroupIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                      <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.primary' }}>
                        Attendees ({viewingEvent.attendees.length})
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {viewingEvent.attendees.map((attendee) => (
                        <Chip
                          key={attendee.user_id}
                          label={`${attendee.user?.full_name || attendee.user?.email || 'Unknown'}${attendee.is_organizer ? ' (Organizer)' : ''}`}
                          size="small"
                          avatar={
                            <ProfileAvatar
                              userId={attendee.user_id}
                              user={attendee.user}
                              size={16}
                              sx={{ 
                                width: '16px !important', 
                                height: '16px !important',
                                fontSize: '0.7rem !important'
                              }}
                            />
                          }
                          sx={{ 
                            borderRadius: 2,
                            fontSize: '0.8rem',
                            backgroundColor: attendee.is_organizer ? alpha(theme.palette.primary.main, 0.1) : alpha(theme.palette.grey[100], 0.8),
                            color: attendee.is_organizer ? 'primary.main' : 'text.primary'
                          }}
                        />
                      ))}
                    </Box>
                  </Box>
                )}

                {/* Recurring Event Info */}
                {viewingEvent.is_recurring && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <RepeatIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                    <Typography variant="body2" sx={{ color: 'text.primary' }}>
                      Repeats {viewingEvent.recurrence_pattern} {viewingEvent.recurrence_interval > 1 ? `every ${viewingEvent.recurrence_interval} ${viewingEvent.recurrence_pattern}s` : ''}
                    </Typography>
                  </Box>
                )}


              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 4, pt: 2, display: 'flex', justifyContent: 'space-between' }}>
            <Button 
              onClick={handleEventViewClose} 
              variant="outlined"
              sx={{ 
                borderRadius: 3, 
                fontWeight: 500,
                px: 3,
                py: 1.5,
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`
              }}
            >
              Close
            </Button>
            
            <Box sx={{ display: 'flex', gap: 2 }}>
              {canDelete && (
                <Button 
                  onClick={handleEventDelete}
                  variant="outlined"
                  color="error"
                  sx={{ 
                    borderRadius: 3, 
                    fontWeight: 500,
                    px: 3,
                    py: 1.5,
                    border: `1px solid ${alpha(theme.palette.error.main, 0.8)}`,
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.error.main, 0.1)
                    }
                  }}
                >
                  <DeleteIcon sx={{ mr: 1, fontSize: 18 }} />
                  Delete
                </Button>
              )}
              
              {canEdit && (
                <Button 
                  onClick={handleEventEdit}
                  variant="contained"
                  sx={{ 
                    borderRadius: 3, 
                    fontWeight: 500,
                    px: 3,
                    py: 1.5,
                    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                    '&:hover': {
                      boxShadow: `0 6px 16px ${alpha(theme.palette.primary.main, 0.4)}`
                    }
                  }}
                >
                  Edit Event
                </Button>
              )}
            </Box>
          </DialogActions>
        </Dialog>

        {/* Quick Add Dialog */}
        <Dialog
          open={quickAddOpen}
          onClose={handleQuickAddClose}
          maxWidth="md"
          fullWidth
          TransitionComponent={Fade}
          BackdropComponent={Backdrop}
          disableScrollLock={true}
          disablePortal={false}
          disableAutoFocus={true}
          disableEnforceFocus={true}
          BackdropProps={{
            timeout: 500,
            sx: { backgroundColor: alpha(theme.palette.grey[900], 0.4) }
          }}
          PaperProps={{
            sx: {
              position: 'absolute !important',
              borderRadius: 3,
              minHeight: 450,
              maxWidth: 700,
              mx: 'auto',
              contain: 'layout style paint',
              zIndex: 9999
            }
          }}
          sx={{
            zIndex: 9999
          }}
        >
          <DialogTitle sx={{ 
            fontWeight: 500, 
            color: 'primary.main',
            borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`,
            py: 3,
            fontSize: '1.25rem'
          }}>
            New Event for {selectedDate && format(selectedDate, 'MMMM d, yyyy')}
          </DialogTitle>
          <DialogContent sx={{ pt: 4, pb: 3, px: 4, mt: 4 }}>
            {/* Event Title */}
            <TextField
              autoFocus
              fullWidth
              label="Event Title"
              placeholder="Enter event title..."
              value={quickFormData.title}
              onChange={(e) => handleQuickInputChange('title', e.target.value)}
              sx={{ 
                mb: 3,
                mt: 2,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 3,
                  minHeight: 56,
                  backgroundColor: alpha(theme.palette.grey[50], 0.5)
                },
                '& .MuiInputLabel-root': {
                  fontWeight: 500
                }
              }}
            />

            {/* All Day Toggle */}
            <FormControlLabel
              control={
                <Switch
                  checked={quickFormData.all_day}
                  onChange={(e) => handleQuickInputChange('all_day', e.target.checked)}
                />
              }
              label="All Day"
              sx={{ mb: 3 }}
            />
            
            {/* Start/End Date */}
            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
              <DateTimePicker
                label={quickFormData.all_day ? "Start Date" : "Start Date & Time"}
                value={quickFormData.start_date}
                onChange={(newValue) => {
                  handleQuickInputChange('start_date', newValue);
                  if (newValue) {
                    // Always set end date to 1 hour after start date
                    const newEndDate = quickFormData.all_day ? newValue : newValue.add(1, 'hour');
                    handleQuickInputChange('end_date', newEndDate);
                  }
                }}
                format={quickFormData.all_day ? 'MM/DD/YYYY' : (timeFormat === '24hour' ? 'MM/DD/YYYY HH:mm' : 'MM/DD/YYYY h:mm A')}
                views={quickFormData.all_day ? ['year', 'month', 'day'] : ['year', 'month', 'day', 'hours', 'minutes']}
                ampm={timeFormat !== '24hour'}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    sx: {
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 3,
                        backgroundColor: alpha(theme.palette.grey[50], 0.5)
                      },
                      '& .MuiInputLabel-root': {
                        fontWeight: 500
                      }
                    }
                  },
                  popper: {
                    sx: {
                      zIndex: 99999
                    }
                  }
                }}
              />

              <DateTimePicker
                label={quickFormData.all_day ? "End Date" : "End Date & Time"}
                value={quickFormData.end_date}
                onChange={(newValue) => handleQuickInputChange('end_date', newValue)}
                format={quickFormData.all_day ? 'MM/DD/YYYY' : (timeFormat === '24hour' ? 'MM/DD/YYYY HH:mm' : 'MM/DD/YYYY h:mm A')}
                views={quickFormData.all_day ? ['year', 'month', 'day'] : ['year', 'month', 'day', 'hours', 'minutes']}
                ampm={timeFormat !== '24hour'}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    sx: {
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 3,
                        backgroundColor: alpha(theme.palette.grey[50], 0.5)
                      },
                      '& .MuiInputLabel-root': {
                        fontWeight: 500
                      }
                    }
                  },
                  popper: {
                    sx: {
                      zIndex: 99999
                    }
                  }
                }}
              />
            </Box>

            {/* Attendees */}
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Add Attendees</InputLabel>
              <Select
                multiple
                value={quickAttendees.filter(a => a && a.user_id).map(a => a.user_id)}
                label="Add Attendees"
                disabled={!canEdit}
                onChange={(e) => {
                  const selectedIds = e.target.value;            
                  // Add new attendees
                  selectedIds.forEach(id => {
                    if (!quickAttendees.find(a => a.user_id === id)) {
                      handleAddAttendee(id);
                    }
                  });
                  // Remove unselected attendees (except organizer)
                  quickAttendees.forEach(attendee => {
                    if (attendee && attendee.user_id && !selectedIds.includes(attendee.user_id) && !attendee.is_organizer) {
                      handleRemoveAttendee(attendee.user_id);
                    }
                  });
                }}
                MenuProps={{
                  disableScrollLock: true,
                  disablePortal: false,
                  disableAutoFocus: true,
                  disableEnforceFocus: true,
                  PaperProps: {
                    sx: {
                      position: 'absolute !important',
                      minWidth: 300,
                      contain: 'layout style paint',
                      zIndex: 9999,
                      borderRadius: 3,
                      border: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
                      boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
                    }
                  },
                  sx: {
                    zIndex: 9999
                  }
                }}
                sx={{ borderRadius: 3, backgroundColor: alpha(theme.palette.grey[50], 0.5) }}
                renderValue={() => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {quickAttendees.filter(attendee => attendee && attendee.user_id).map((attendee) => (
                      <Chip
                        key={attendee.user_id}
                        label={`${attendee.user?.full_name || attendee.user?.email || 'Unknown'}${attendee.is_organizer ? ' (Organizer)' : ''}`}
                        size="small"
                        avatar={
                          <ProfileAvatar
                            userId={attendee.user_id}
                            user={attendee.user}
                            size={16}
                            sx={{ 
                              width: '16px !important', 
                              height: '16px !important',
                              fontSize: '0.7rem !important'
                            }}
                          />
                        }
                        onDelete={!attendee.is_organizer && canEdit ? () => handleRemoveAttendee(attendee.user_id) : undefined}
                        sx={{ 
                          borderRadius: 2, 
                          fontSize: '0.8rem',
                          backgroundColor: attendee.is_organizer ? alpha(theme.palette.primary.main, 0.1) : 'default'
                        }}
                      />
                    ))}
                  </Box>
                )}
              >
                {users.filter(user => user && user.id && typeof user.id === 'string').map((user) => (
                  <MenuItem key={user.id} value={user.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <ProfileAvatar
                        userId={user.id}
                        user={user}
                        size={18}
                        sx={{ fontSize: '0.7rem' }}
                      />
                      <Typography sx={{ fontSize: '0.9rem' }}>
                        {user.full_name || user.email}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Video Call URL and Password */}
            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
              <TextField
                fullWidth
                label="Video Call URL"
                value={quickFormData.videocall_url}
                onChange={(e) => handleQuickInputChange('videocall_url', e.target.value)}
                disabled={!canEdit}
                sx={{ 
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 3,
                    backgroundColor: alpha(theme.palette.grey[50], 0.5)
                  }
                }}
                InputProps={{
                  startAdornment: <VideoCallIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />
                }}
              />
              
              <TextField
                fullWidth
                label="Meeting Password"
                value={quickFormData.meeting_password}
                onChange={(e) => handleQuickInputChange('meeting_password', e.target.value)}
                disabled={!canEdit}
                sx={{ 
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 3,
                    backgroundColor: alpha(theme.palette.grey[50], 0.5)
                  }
                }}
                InputProps={{
                  startAdornment: <LockIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />
                }}
              />
            </Box>

            {/* Location */}
            <TextField
              fullWidth
              label="Location"
              value={quickFormData.location}
              onChange={(e) => handleQuickInputChange('location', e.target.value)}
              disabled={!canEdit}
              sx={{ 
                mb: 3,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 3,
                  backgroundColor: alpha(theme.palette.grey[50], 0.5)
                }
              }}
              InputProps={{
                startAdornment: <LocationIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />
              }}
            />

            {/* Description */}
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Description"
              placeholder="Enter event description..."
              value={quickFormData.description}
              onChange={(e) => handleQuickInputChange('description', e.target.value)}
              disabled={!canEdit}
              sx={{
                mb: 3,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 3,
                  backgroundColor: alpha(theme.palette.grey[50], 0.5)
                }
              }}
            />


          </DialogContent>
          <DialogActions sx={{ p: 4, pt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button 
                onClick={handleQuickAddClose} 
                variant="outlined"
                sx={{ 
                  borderRadius: 3, 
                  fontWeight: 500,
                  px: 3,
                  py: 1.5,
                  minWidth: 100,
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`
                }}
              >
                Cancel
              </Button>
              <Button 
                onClick={handleQuickAdd} 
                variant="contained" 
                disabled={!canEdit || !quickFormData.title.trim() || 
                  (quickFormData.start_date && quickFormData.end_date && 
                   new Date(quickFormData.end_date) <= new Date(quickFormData.start_date))}
                sx={{ 
                  borderRadius: 3, 
                  fontWeight: 500,
                  px: 3,
                  py: 1.5,
                  minWidth: 120,
                  boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                  '&:hover': {
                    boxShadow: `0 6px 16px ${alpha(theme.palette.primary.main, 0.4)}`
                  },
                  '&:disabled': {
                    opacity: 0.6,
                    boxShadow: 'none'
                  }
                }}
              >
                Save Event
              </Button>
            </Box>

            <Button 
              onClick={handleMoreOptions}
              variant="text"
              disabled={!canEdit}
              sx={{ 
                borderRadius: 3, 
                fontWeight: 500,
                px: 3,
                py: 1.5,
                color: 'primary.main',
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.04)
                },
                '&:disabled': {
                  opacity: 0.6,
                  color: 'text.disabled'
                }
              }}
            >
              More Options
            </Button>
          </DialogActions>
        </Dialog>
      </CalendarContainer>
    </LocalizationProvider>
  );
};

export default CalendarView; 